import React from 'react';
import { computed, observable, action, reaction, makeObservable } from 'mobx';

import { TextField, mergeStyleSets, FontSizes, FontWeights, ContextualMenuItemType } from '@fluentui/react';
// import { makeStyles, FontWeights, ContextualMenu, , ContextualMenuItem, Icon } from '@fluentui/react';
import CellAddressPicker from '@v2/components/base/CellAddressPicker';
import { NEW_WORKSHEET_ID } from '@v2/components/base/CellAddressPicker/hooks/useCellAddressValidation';
import GroupPane from 'src/components/base/GroupPane';
import DefaultButtonEx from '@v2/components/base/DefaultButtonEx'
import MultiButton from '@v2/components/base/MultiButton';
import ConfirmDialog from 'src/components/base/ConfirmDialog';
import SearchableComboBox from 'src/components/base/SearchableComboBox';

import { getStepTitleGenerator, Step } from 'src/components/Flowsteps/base/Step';

import office from 'src/services/office';

import { runCancelableAction, runShortAction } from 'src/services/utils';
import { Type, FlowstepCategory, toCategory, generateUuid, ErrorReaction } from 'shared/utils/flowstep';
import { LEGACY_CURRENT_VERSION } from 'shared/migration/flowstep';
import * as Flow from 'shared/utils/flow';
import CheckboxWithTooltip from 'src/components/base/CheckboxWithTooltip';
import { observer } from 'mobx-react';


export const classNames = mergeStyleSets({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
  },

  content: {
    margin: '0 16px',
    selectors: {
      '& > div': {
        marginTop: '16px',
      },
    }
  },

  buttonsBlock: {
    display: 'flex',
    flexFlow: 'row',
    justifyContent: 'space-between',
    margin: '8px 0 8px -16px',

    selectors: {
      '& > *': {
        flexGrow: 1,
        flexBasis: 0,
        marginLeft: '16px',
      },
    },
  },

  options: {
    margin: '16px 0px 0px 0px',
  },

  optionsRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'start',
  },

  optionsContent: {
    margin: '16px 16px 0px 16px',
    selectors: {
      '& > div': {
        // display: 'flex',
        // flexDirection: 'row',
        // justifyContent: 'space-between',
        // alignItems: 'center',
        marginTop: '16px',
      },
    },
  },

  multiButtonMenuFix: {
    minWidth: '100px',
  }
});


export const Button = {
  Save: 'Save',
  SaveCopy: 'Save copy',
  Run: 'Run',
  RunAndSave: 'Run and save',
  Delete: 'Delete',
  Move: 'Move'
};


const Data = {
  textFieldStyles: { wrapper: { width: '100%' } },
  defaultErrorReaction: ErrorReaction.AskUser,
  ErrorReactions: [
    {
      key: ErrorReaction.Continue, text: 'Continue',
      // tooltip: 'If execution fails for any reason, the following flow step (if any) will be started without delay'
    },
    {
      key: ErrorReaction.Stop, text: 'Stop',
      // tooltip: 'If execution fails for any reason, the error message will be displayed , and no further flow steps will be executed in the current flow',
    },
    {
      key: ErrorReaction.AskUser, text: 'Ask user',
      // tooltip: `If execution fails for any reason, the error message will be displayed, and the user will be asked whether they want to stop or continue. When executed unattended from the Scheduler, 'Ask User' setting is considered as 'Stop'`
    }
  ],
};

class Flowstep extends React.Component {
  @observable flowId = '';
  @observable flowstep = null;
  @observable type = Type.Soql;
  @observable types = [Type.Soql];
  @observable name = '';

  @observable selectionTarget = null;

  @observable isDirty = false;
  ignoreIsDirty = true;
  @observable showDeleteConfirmation = false;

  @observable options = {
    lastUpdateTimestampCell: '',
    lastUpdateTimestampCellAddress: null,
    errorReaction: Data.defaultErrorReaction,
    createNewSheet: null,
    protectionPassword: '',
    recalculateFormulas: false,
  };

  @observable areValid = {
    lastUpdateTimestampCellAddress: true,
  };

  constructor(reactProps, flowstepProps) {
    super(reactProps);

    const { type } = flowstepProps;
    this.type = type;
    this.types = [type];


    this.cleanReactions = () => { };
  }

  setupBaseReactions() {
    const r1 = reaction(
      () => ({ isDirty: this.isDirty, isValid: this._isValid_, flowstep: this.flowstep }),
      () => {
        this.props.pageControl.setDirtyActions(this.isDirty && this.flowstep
          ? {
            text: 'You have unsaved changes in the flow step',
            onSave: () => this.handleSaveButtonClick({ key: Button.Save }),
            disableSave: !this._isValid_ || this._readOnly_
          }
          : null);
      });

    const r2 = reaction(
      () => ({ article: this._helpArticleLink_ }),
      ({ article }) => this.props.pageControl.setHelpLink(article)
    );

    const r3 = reaction(
      () => ({ name: this.name, flowstep: this.flowstep, isDirty: this.isDirty }),
      () => this.updatePageTitle(),
    );

    return [r1, r2, r3];
  }

  componentWillUnmount() {
    this.cleanReactions();
  }

  // TODO: to _pageTitle_
  @computed get pageTitle() {
    // return toPageTitle(this.type);
    const star = this.isDirty ? '*' : '';
    const prefix = this.flowstep ? 'Editing' : 'New';
    return `${prefix}: ${star}${this.displayName}`;
  }

  @computed get areAllOptionsValid() {
    return Object.values(this.areValid).every(x => !!x);
  }

  @computed get license() {
    return this.props.officeStore.userStore.license;
  }

  @computed get officeStore() {
    return this.props.officeStore;
  }

  @computed get dataStore() {
    return this.officeStore.dataStore;
  }

  @computed get contextStore() {
    return this.officeStore.contextStore;
  }

  @computed get showName() {
    return this.license.flowsteps.seeName.can;
  }

  @computed get category() {
    return toCategory({ type: this.type });
  }

  @computed get displayName() {
    return this.name || this._generatedName_;
  }

  @computed get flowsMoveTo() {
    const { flows, flowsteps } = this.license;
    const { addFlowsteps } = flows.custom;
    const { unlimited } = flowsteps.push.createOrRemove;

    return this.officeStore.flows.reduce((out, { id, type, name }) => {
      if (id === this.flowId) { return out; }
      if ((Flow.Type.Custom === type && addFlowsteps.can)
        || (this.category === FlowstepCategory.Pull && Flow.Type.Pull === type)
        || (this.category === FlowstepCategory.Push && Flow.Type.Push === type && unlimited.can)) {
        out.push({ key: id, id, text: name });
      }
      return out;
    }, []);
  }

  @computed get buttons() {
    const { hasPersonalPlan } = this.license;
    const { allowSaveAndDelete = true, allowSaveCopy = true, allowEditExisting = true, data } = this._buttons_;

    // For 'Personal' plan we allow to save flowstep (only one per type, see componentDidMount),
    // but the button still has to have 'Run' label
    // The same is true for a tooltip as well
    const showSaveAndDelete = !hasPersonalPlan && allowSaveAndDelete;
    const runAndSaveKey = hasPersonalPlan ? Button.Run : Button.RunAndSave;
    const { tooltip, text } = data[runAndSaveKey];
    const runItems = toButtonMenu([
      allowEditExisting && { key: Button.RunAndSave, text: text ?? runAndSaveKey, tooltip },
      !hasPersonalPlan && { key: Button.Run, },
    ], data);

    const canSaveCopy = !!this.flowstep && allowSaveCopy;
    const subMenuProps = { items: this.flowsMoveTo };
    const withAction = ({ items, ...rest }, action) => ({ ...rest, items: items.map(x => ({ ...x, action })) });
    const saveItems = toButtonMenu([
      { key: Button.Save },
      canSaveCopy && !!this.flowsMoveTo.length && {
        key: 'copy-to',
        text: 'Save copy to flow',
        tooltip: 'Adds the copy of this flow step to a different flow',
        subMenuProps: withAction(subMenuProps, Button.SaveCopy),
      },
      !!this.flowstep && !!this.flowsMoveTo.length && {
        key: Button.Move,
        text: 'Move to flow',
        tooltip: 'Moves this flow step to a different flow',
        subMenuProps: withAction(subMenuProps, Button.Move),
      },
      canSaveCopy && { key: 'separator', itemType: ContextualMenuItemType.Divider },
      canSaveCopy && { key: Button.SaveCopy, },
    ], data);

    return { showSaveAndDelete, saveItems, runItems };
  }

  getLinkedSheetId() {
    return this.options.createNewSheet ? NEW_WORKSHEET_ID : this.flowstep?.worksheetId ?? this.props.officeStore.activeWorksheetId;
  }

  render() {
    const renderProps = { getStepTitle: getStepTitleGenerator() };
    const readOnly = this._readOnly_ ?? false;
    const { showSaveAndDelete, runItems, saveItems } = this.buttons;
    return (
      <div className={classNames.root}>
        {this._renderBody_(renderProps)}

        <GroupPane title='Options' className={classNames.options}>
          <div className={classNames.optionsContent}>
            {this._renderCustomOptions_(renderProps)}
            <CheckboxWithTooltip
              label={'Force formula recalculation when done'}
              tooltip={'Recalculates all workbook formulas and pivot tables after execution of this flow step'}
              checked={this.options.recalculateFormulas}
              onChange={(_, checked) => this.handleOptionsChange('recalculateFormulas', checked)}
            />
          </div>
        </GroupPane>

        <GroupPane title='Advanced' className={classNames.options}>
          <div className={classNames.optionsContent}>
            {this.showName &&
              <TextField
                label="Flow step name"
                required
                styles={Data.textFieldStyles}
                disabled={readOnly}
                placeholder={this._generatedName_}
                value={this.name}
                onChange={(event, value) => this.handleNameChange(value)}
              />
            }

            <SearchableComboBox
              placeholder={`Reaction on error`}
              label="Reaction on error"
              selectedKey={this.options.errorReaction}
              onChange={(event, item) => this.handleOptionsChange('errorReaction', item.key)}
              options={Data.ErrorReactions}
              disabled={this._readOnly_}
            />


            <TextField
              label="Sheet Protection password"
              styles={Data.textFieldStyles}
              disabled={readOnly}
              placeholder={'Password'}
              value={this.options.protectionPassword}
              onChange={(event, value) => this.handleOptionsChange('protectionPassword', value)}
            />

            <CellAddressPicker
              label={'Last run timestamp cell'}
              tooltip={`If you specify a cell address here, we'll write the current timestamp value to it every time this flow step is executed`}
              allowEmptyCell={true}
              disabled={readOnly}
              value={this.options.lastUpdateTimestampCellAddress}
              onChange={(value, isValid) => this.handleOptionsAndIsValidChange('lastUpdateTimestampCellAddress', value, isValid)}
              allowNewSheet={this.options.createNewSheet}
              localAddressSheetIdGetter={() => this.getLinkedSheetId()}
            />
          </div>
        </GroupPane>


        <div className={classNames.content}>
          <div className={classNames.buttonsBlock}>
            <MultiButton
              items={runItems}
              disabled={!this._isValid_}
              primary={true}
              onClick={(event, item) => this.handleRunButtonClick(item)}
            />
          </div>

          {showSaveAndDelete &&
            <div className={classNames.buttonsBlock}>
              <DefaultButtonEx
                text={Button.Delete}
                tooltip={'Deletes the current flow step from the worksheet'}
                disabled={!this.flowstep || readOnly}
                onClick={() => this.showDeleteConfirmation = true}
              />

              <MultiButton
                items={saveItems}
                disabled={!this._isValid_ || readOnly}
                primary={true}
                onClick={(event, item) => this.handleSaveButtonClick(item)}
              // menuProps={{ doNotLayer: true, focusZoneProps: { style: { minWidth: '90px' }, className: classNames.multiButtonMenuFix }}}
              />
            </div>
          }
        </div>


        <ConfirmDialog
          title={'Delete the Flow Step?'}
          text={'Are you sure you want to permanently remove this flow step?'}
          hidden={!this.showDeleteConfirmation}
          onConfirm={this.handleDeleteFlowstep}
          onDismiss={() => this.showDeleteConfirmation = false}
        />

        {this._renderCustomPages_?.(renderProps)}

      </div>
    );
  }

  renderErrorReaction({ getStepTitle }, { name, action }) {
    return (
      <Step title={getStepTitle(`Define the behavior on error when this ${name} is ${action} as part of a flow`)}>
        <SearchableComboBox
          placeholder={`Reaction on error`}
          selectedKey={this.options.errorReaction}
          onChange={(event, item) => this.handleOptionsChange('errorReaction', item.key)}
          options={Data.ErrorReactions}
          disabled={this._readOnly_}
        />
      </Step>
    );
  }

  async componentDidMount() {
    const { flowId, createNew, pageControl, computedMatch } = this.props;
    const { id } = computedMatch.params;
    pageControl.setHelpLink(this._helpArticleLink_);
    Object.assign(this, { flowId });

    // Workaround: sometimes this.isDirty is set on load because of callback call
    // to 'handleOptionsChange' (mapping, rangeToApply, etc.). So, this flag is set to
    // ignore isDirty until loading is completed
    this.ignoreIsDirty = true;

    await runShortAction(async () => {
      const data = await this._componentDidMountStart_?.({ ...this.props, id });

      if (id) {
        const flowstep = this.props.dataStore.getFlowstep(id);
        await this._loadFlowstepOnMount_(flowstep);
      }
      else {
        this.setSelectionTarget(await office.getFlowstepTarget());
        await this.findAndLoadSimilarFlowstep(!createNew);
      }

      if (!this.flowId && this.flowstep) {
        this.flowId = this.props.dataStore.getFlowByFlowstep(this.flowstep.id)?.id ?? '';
      }

      this.ignoreIsDirty = false;
      await this.contextStore.updateNamedRanges();
      await this._componentDidMountEnd_?.(data);
    });

    this.updatePageTitle();
  }

  async findAndLoadSimilarFlowstep(loadTheSameTypeAsExact) {
    const { worksheetId, targetCell } = this.selectionTarget;
    const allWithSameTarget = this.props.officeStore.flowSteps.filter(x => x.worksheetId === worksheetId && x.target === targetCell);
    const allSimilarTypesWithSameTarget = allWithSameTarget.filter(x => this.types.includes(x.type));
    const theSameType = allSimilarTypesWithSameTarget.find(x => x.type === this.type);
    if (allSimilarTypesWithSameTarget.length) {
      const isExact = loadTheSameTypeAsExact && !!theSameType;
      const flowstep = theSameType ?? allSimilarTypesWithSameTarget[0];
      await this._loadFlowstep_(flowstep, isExact);
      return;
    }

    await this._tryLoadFlowstepOfDifferentType_?.(allWithSameTarget, allSimilarTypesWithSameTarget);
  }

  loadFlowstep(flowstep, isExact) {
    if (isExact) {
      this.flowstep = flowstep;
    }

    this.name = flowstep.name;
    this.type = flowstep.type;
    const { lastUpdateTimestampCell, lastUpdateTimestampCellAddress, errorReaction, protectionPassword, recalculateFormulas } = flowstep.options;

    const newlastUpdateTimestampCellAddress = lastUpdateTimestampCellAddress ??
      (lastUpdateTimestampCell ? { worksheetId: flowstep.worksheetId, address: lastUpdateTimestampCell } : null);

    this.options = {
      ...this.options,
      lastUpdateTimestampCell,
      lastUpdateTimestampCellAddress: newlastUpdateTimestampCellAddress,
      errorReaction: errorReaction ?? Data.defaultErrorReaction,
      protectionPassword: protectionPassword,
      recalculateFormulas: recalculateFormulas ?? false,
    };
  }

  makeFlowstep(makeCopy = false) {
    const { lastUpdateTimestampCellAddress, lastUpdateTimestampCell, errorReaction, recalculateFormulas } = this.options;
    let { protectionPassword } = this.options;
    if (protectionPassword === "") {
      protectionPassword = undefined;
    }
    const flowstep = {
      id: (makeCopy || !this.flowstep?.id) ? generateUuid() : this.flowstep.id,
      name: this.displayName,
      type: this.type,
      version: LEGACY_CURRENT_VERSION,
      category: this.category,
      options: { lastUpdateTimestampCellAddress, lastUpdateTimestampCell, errorReaction, protectionPassword, recalculateFormulas },
    };

    return flowstep;
  }

  setSelectionTarget(target) {
    this.selectionTarget = target;
    this._setSelectionTarget_?.(target);
  }

  @action setDirty = (isDirty = true) => {
    if (!this.ignoreIsDirty) {
      this.isDirty = isDirty;
      this._setDirty_?.(isDirty);
    }
  }

  updatePageTitle() {
    this.props.pageControl.setTitle(this.pageTitle)
  }

  handleRunButtonClick = async (item) => {
    const { key } = item;

    await runCancelableAction(async (cancelToken) => {
      const flowstep = await this._makeFlowstep_(false);

      await this.officeStore.executeFlowstep(flowstep, cancelToken);
      if (Button.RunAndSave === key) {
        this.flowId = await this.dataStore.createOrUpdateFlowstep(flowstep, this.flowId);
        this.flowstep = flowstep;
        this.setDirty(false);
        this.goToFlow();
      }
    });
  }

  handleSaveButtonClick = async (item) => {
    const { key, action } = item;

    const buttonKey = action ?? key;
    const flowId = action ? item.id : this.flowId;
    const saveCopy = Button.SaveCopy === buttonKey;
    await runShortAction(async () => {
      const flowstep = await this._makeFlowstep_(saveCopy);

      this.flowId = buttonKey === Button.Move
        ? await this.dataStore.moveFlowstepToFlow(flowstep, this.flowId, flowId)
        : await this.dataStore.createOrUpdateFlowstep(flowstep, flowId);

      this.flowstep = flowstep;
      this.setDirty(false);
      this.goToFlow();
    });
  }

  handleDeleteFlowstep = async () => {
    this.showDeleteConfirmation = false;

    await runShortAction(async () => {
      await this.dataStore.deleteFlowstep(this.flowstep, this.flowId);
      this.flowstep = null;
      this.setDirty(false);
      this.goToFlow();
    });
  }

  goToFlow() {
    const route = Flow.getRoute({ selectedId: this.flowId });
    this.license.flows.seeTabContent.can && this.flowId
      ? this.props.history.replace(route)
      // : this.props.goBack();
      : this.props.history.goBack();
  }

  @action handleOptionsChange(index, value) {
    this.options[index] = value;
    this.setDirty();
  }

  @action handleOptionsAndIsValidChange = (index, value, isValid) => {
    this.handleOptionsChange(index, value);
    this.areValid[index] = isValid;
  }

  @action handleNameChange = (value) => {
    this.name = value;
    this.setDirty();
  }
}


function toButtonMenu(buttons, data) {
  return buttons.reduce((out, x) => {
    x && out.push({
      text: x.text ?? x.key, ...data[x.key], ...x,
    });
    return out;
  }, []);
}


export default observer(Flowstep);
